import { Box, Button, Dialog, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { campaign } from '../../Utils/apiService';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import { AuthUser } from '../../interfaces/Contexts';
import KenLoader from '../../GlobalComponents/KenLoader';
import { useStyles } from './styles';
import CreateCampaign from './CreateCampaign';

interface CreateCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateCampaignDialog: React.FC<CreateCampaignDialogProps> = ({ open, onClose, onSuccess }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const method = useForm({ mode: 'onChange' });
  const { reset, handleSubmit } = method;
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleCreateCampaign = (data: any) => {
    setLoading(true);
    const payload = {
      ...data,
      accountId: userData.applicantId,
      fundCategory: data?.fundCategory === 'Others' ? data?.fundCategoryOther : data?.fundCategory,
      goalAmount: parseFloat(data?.goalAmount),
    };
    delete payload['fundCategoryOther'];

    campaign(payload)
      .then((res) => {
        if (res?.success === 'true') {
          enqueueSnackbar({ message: 'Campaign created successfully', variant: 'success', autoHideDuration: 1000 });
          onSuccess?.();
          handleClose();
        } else {
          enqueueSnackbar({ message: 'Something went wrong, please try again', variant: 'error', autoHideDuration: 1000 });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar({ message: 'Something went wrong, please try again', variant: 'error', autoHideDuration: 1000 });
      });
  };

  return (
    <>
      {loading && <KenLoader />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <FormProvider {...method}>
          <Box
            component="form"
            onSubmit={handleSubmit((data) => {
              handleCreateCampaign(data);
            })}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            {isMobile ? (
              <>
                <Box sx={{ px: 2, pt: 2 }}>
                  <Typography className={classes.registeredEventsHeading} sx={{ fontSize: '20px !important' }}>
                    Campaign Details
                  </Typography>
                  <div className={classes.divider} />
                </Box>
                <Box sx={{ px: 2, pb: 2, flex: 1, minHeight: 0, overflowY: 'auto' }} className="scrollbar-cusgray">
                  <CreateCampaign />
                </Box>
                <Box
                  sx={{
                    padding: 2,
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    background: '#fff',
                  }}
                >
                  <Button size="medium" variant="contained" type="button" color="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button size="medium" variant="contained" type="submit" color="primary">
                    Submit Request
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ flexShrink: 0, background: '#fff', zIndex: 2, px: 3, pt: 2 }}>
                  <Typography className={classes.registeredEventsHeading} sx={{ fontSize: '20px !important', mb: 2 }}>
                    Campaign Details
                  </Typography>
                  <div className={classes.divider} />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, background: '#fff', px: 3, pb: 3 }}>
                  <CreateCampaign />
                </Box>
                <Box
                  sx={{
                    padding: 2,
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    flexShrink: 0,
                    background: '#fff',
                  }}
                >
                  <Button size="medium" variant="contained" color="secondary" type="button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button size="medium" color="primary" variant="contained" type="submit">
                    Submit Request
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </FormProvider>
      </Dialog>
    </>
  );
};

export default CreateCampaignDialog;
